/**
 * Conversion par cohorte d'inscription.
 *
 * La hauteur de barre EST le taux de conversion de la cohorte, découpé selon
 * l'état actuel : encore abonnés d'un côté, résiliés de l'autre. Deux
 * informations sur une seule longueur, sans que le lecteur ait à faire une
 * division de tête.
 *
 * ⚠️ Piège de lecture, dit dans le sous-titre : les cohortes anciennes ont eu
 * plus de temps pour convertir ET plus de temps pour résilier. Les deux effets
 * se confondent. C'est précisément pourquoi la courbe de survie plus bas existe
 * — elle normalise par l'âge de l'abonnement.
 */

import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatInteger, formatMonthShort, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, SignupCohortRow } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';

const legend: readonly LegendEntry[] = [
  { label: 'Encore abonnés', color: COLORS.series1, shape: 'area' },
  { label: 'Résiliés', color: COLORS.series2, shape: 'area' },
];

const columns: ReadonlyArray<DataColumn<SignupCohortRow>> = [
  { key: 'cohort', header: 'Inscrits en', render: (row) => formatMonthShort(row.cohort) },
  { key: 'accounts', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.accounts) },
  { key: 'converted', header: 'Convertis', numeric: true, render: (row) => formatInteger(row.converted) },
  { key: 'conv', header: 'Conversion', numeric: true, render: (row) => formatPercent(row.conversionPercent) },
  { key: 'still', header: 'Encore abonnés', numeric: true, render: (row) => formatInteger(row.stillSubscribed) },
  { key: 'churn', header: 'Résiliés', numeric: true, render: (row) => formatInteger(row.churned) },
  {
    key: 'ret',
    header: 'Toujours abonnés',
    numeric: true,
    render: (row) => (row.retentionPercent === null ? '—' : formatPercent(row.retentionPercent)),
  },
];

interface Row extends SignupCohortRow {
  stillPct: number;
  churnedPct: number;
}

export function SignupCohortChart({ data }: { data: readonly SignupCohortRow[] }) {
  // Les deux parts sont exprimees en % de la COHORTE, pas des convertis : leur
  // somme est donc le taux de conversion, et les barres se comparent d'une
  // cohorte a l'autre quelle que soit sa taille.
  const rows: Row[] = data.map((row) => ({
    ...row,
    stillPct: row.accounts > 0 ? (row.stillSubscribed / row.accounts) * 100 : 0,
    churnedPct: row.accounts > 0 ? (row.churned / row.accounts) * 100 : 0,
  }));

  return (
    <ChartCard
      title="Conversion par cohorte d'inscription"
      subtitle="Hauteur = taux de conversion — attention, les cohortes anciennes ont eu plus de temps pour convertir et pour résilier"
      emptyMessage="Aucun compte inscrit sur cette période."
      className="chart-grid__wide"
      rows={rows}
      columns={columns}
      chartHeight={320}
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="88%">
        <BarChart data={rows} margin={{ top: 20, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="cohort"
            tickFormatter={formatMonthShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={20}
          />
          <YAxis
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip labelFormatter={formatMonthShort} valueFormatter={formatPercent} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey="stillPct"
            stackId="conv"
            name="Encore abonnés"
            fill={COLORS.series1}
            stroke={COLORS.surface}
            strokeWidth={2}
            maxBarSize={BAR_MAX_SIZE}
          />
          <Bar
            isAnimationActive={false}
            dataKey="churnedPct"
            stackId="conv"
            name="Résiliés"
            fill={COLORS.series2}
            stroke={COLORS.surface}
            strokeWidth={2}
            radius={[4, 4, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            {/* Etiquette posee au sommet de la barre ENTIERE : c'est le taux de
                conversion, la somme des deux segments. */}
            <LabelList
              dataKey="conversionPercent"
              position="top"
              fill={COLORS.textSecondary}
              fontSize={11}
              formatter={(value: unknown) => formatPercent(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
